/**
 * Teach cards — one person's state, and the one-card-at-a-time slot (#1838).
 *
 * Mounted once in the app shell. Reads GET /api/teach/state (switch off →
 * `on: false`, and everything below renders nothing). Writes go to the
 * person's own user_preferences rows through /api/teach/*, so what they've
 * seen follows them to any device.
 *
 * markUsed is batched: the keys a person uses in a burst go up in one POST,
 * and each key only once per page load.
 *
 * One teaching item per page load (Corbin 10/10): the first card or
 * spotlight that takes the slot is the only one this load. Once it's done,
 * nothing else claims the slot until the next load — so the Help Desk/Tasks
 * spotlight and a screen's intro card never show on the same visit. (The
 * spotlight's Help Desk → Tasks steps are one item.) What's new replays and
 * office dots are not slot items.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { feature } from "./features";
import type { TeachRole } from "./features";
import { EMPTY_TEACH_STATE, introEligible, type TeachStateData } from "./rules";
import { TeachSpotlight } from "./teach-spotlight";

export const TEACH_STATE_KEY = ["teach", "state"] as const;

interface TeachContextValue {
    state: TeachStateData;
    role: TeachRole;
    /** The server has answered (on or off). */
    ready: boolean;
    /** The one card/spotlight on screen right now, if any. */
    active: string | null;
    claim: (key: string) => void;
    release: (key: string) => void;
    markUsed: (key: string) => void;
    markSeen: (key: string) => void;
    snooze: (key: string) => void;
    setTipsOff: (off: boolean) => void;
    spotlightShown: (key: string) => void;
    /** Techs: the screen that may carry the spotlight says so (field home, not mid-visit). */
    techSpotOk: boolean;
    setTechSpotOk: (ok: boolean) => void;
    /** The spotlight is finished for this page load (or nothing was due). Intro cards wait for it. */
    spotSettled: boolean;
    setSpotSettled: (v: boolean) => void;
}

const TeachContext = createContext<TeachContextValue | null>(null);

const OFF: TeachContextValue = {
    state: EMPTY_TEACH_STATE,
    role: "office",
    ready: false,
    active: null,
    claim: () => {},
    release: () => {},
    markUsed: () => {},
    markSeen: () => {},
    snooze: () => {},
    setTipsOff: () => {},
    spotlightShown: () => {},
    techSpotOk: false,
    setTechSpotOk: () => {},
    spotSettled: true,
    setSpotSettled: () => {},
};

/** Outside the provider (a bare component test) everything is off. */
export function useTeach(): TeachContextValue {
    return useContext(TeachContext) ?? OFF;
}

export function TeachProvider({ children }: { children: ReactNode }) {
    const { user, isField, isOffice, isAdmin } = useAuth();
    const role: TeachRole = isField && !isOffice && !isAdmin ? "tech" : "office";
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: TEACH_STATE_KEY,
        queryFn: () => api.get<TeachStateData>("/api/teach/state"),
        enabled: !!user,
        staleTime: 5 * 60_000,
        retry: false,
    });
    // Any error reads as OFF: teaching must never get in the way of work.
    const state = q.data ?? EMPTY_TEACH_STATE;
    const [active, setActive] = useState<string | null>(null);
    const [techSpotOk, setTechSpotOk] = useState(false);
    const [spotSettled, setSpotSettled] = useState(false);

    const patch = useCallback(
        (fn: (s: TeachStateData) => TeachStateData) => {
            qc.setQueryData<TeachStateData>(TEACH_STATE_KEY, (old) => fn(old ?? EMPTY_TEACH_STATE));
        },
        [qc],
    );

    // ── markUsed, batched ────────────────────────────────────────────────
    const sentThisLoad = useRef(new Set<string>());
    const queue = useRef(new Set<string>());
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const flush = useCallback(() => {
        timer.current = null;
        const keys = [...queue.current];
        queue.current.clear();
        if (keys.length === 0) return;
        // Best effort: a lost use only means a dot shows one more time.
        api.post("/api/teach/used", { keys }).catch(() => {
            for (const k of keys) sentThisLoad.current.delete(k);
        });
    }, []);
    const markUsed = useCallback(
        (key: string) => {
            if (!state.on || !feature(key)) return;
            if (sentThisLoad.current.has(key)) return;
            sentThisLoad.current.add(key);
            const now = new Date().toISOString();
            patch((s) => ({
                ...s,
                used: {
                    ...s.used,
                    [key]: s.used[key]
                        ? { ...s.used[key], last: now, count: s.used[key].count + 1 }
                        : { first: now, last: now, count: 1 },
                },
            }));
            queue.current.add(key);
            if (!timer.current) timer.current = setTimeout(flush, 1500);
        },
        [state.on, patch, flush],
    );
    useEffect(
        () => () => {
            if (timer.current) {
                clearTimeout(timer.current);
                flush();
            }
        },
        [flush],
    );

    // ── Clicking a marked button is using it ─────────────────────────────
    // Except where opening isn't using (Help Desk: a question SENT is use).
    useEffect(() => {
        if (!state.on) return;
        const onClick = (e: MouseEvent) => {
            const el = (e.target as Element | null)?.closest?.("[data-teach]");
            const key = el?.getAttribute("data-teach");
            if (!key) return;
            if (feature(key)?.clickIsUse === false) return;
            markUsed(key);
        };
        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, [state.on, markUsed]);

    const markSeen = useCallback(
        (key: string) => {
            const at = new Date().toISOString();
            patch((s) => ({ ...s, seen: { ...s.seen, [key]: s.seen[key] ?? at } }));
            api.post("/api/teach/seen", { key }).catch(() => {
                toast.error("Couldn't save that. The card may show once more.");
            });
        },
        [patch],
    );

    const snooze = useCallback(
        (key: string) => {
            const until = new Date(Date.now() + 14 * 86_400_000).toISOString();
            patch((s) => ({ ...s, snoozed: { ...s.snoozed, [key]: until } }));
            api.post("/api/teach/snooze", { key }).catch(() => {
                qc.invalidateQueries({ queryKey: TEACH_STATE_KEY });
                toast.error("Couldn't save Not now. The dot is back.");
            });
        },
        [patch, qc],
    );

    const setTipsOff = useCallback(
        (off: boolean) => {
            patch((s) => ({ ...s, tipsOff: off }));
            api.put("/api/teach/tips-off", { off }).catch(() => {
                qc.invalidateQueries({ queryKey: TEACH_STATE_KEY });
                toast.error("Couldn't save the tips switch. It's back the way it was.");
            });
        },
        [patch, qc],
    );

    const spotlightShown = useCallback(
        (key: string) => {
            const at = new Date().toISOString();
            patch((s) => ({ ...s, spotlight: { ...s.spotlight, [key]: [...(s.spotlight[key] ?? []), at] } }));
            // Best effort: a lost showing means it may come back a week early.
            api.post("/api/teach/spotlight-shown", { key }).catch(() => {});
        },
        [patch],
    );

    // The one item taught this page load: the first key to take the slot.
    const taught = useRef<string | null>(null);
    useEffect(() => {
        if (active && !taught.current) taught.current = active;
    }, [active]);
    const claim = useCallback(
        (key: string) => setActive((cur) => cur ?? (taught.current === null || taught.current === key ? key : null)),
        [],
    );
    const release = useCallback((key: string) => setActive((cur) => (cur === key ? null : cur)), []);

    const value = useMemo<TeachContextValue>(
        () => ({
            state,
            role,
            ready: q.isFetched || q.isError,
            active,
            claim,
            release,
            markUsed,
            markSeen,
            snooze,
            setTipsOff,
            spotlightShown,
            techSpotOk,
            setTechSpotOk,
            spotSettled,
            setSpotSettled,
        }),
        [
            techSpotOk,
            spotSettled,
            state,
            role,
            q.isFetched,
            q.isError,
            active,
            claim,
            release,
            markUsed,
            markSeen,
            snooze,
            setTipsOff,
            spotlightShown,
        ],
    );
    return (
        <TeachContext.Provider value={value}>
            {children}
            <TeachSpotlight />
        </TeachContext.Provider>
    );
}

/**
 * An intro card for `key`, shown the first time this person lands where the
 * feature lives. `when` is the screen's own "the feature is here and on" (the
 * new Grid switch, the No Job Left Open flag…). Waits for the spotlight, and
 * for any other card, so two never show at once.
 */
export function useTeachIntro(key: string, when: boolean): { show: boolean; done: () => void; later: () => void } {
    const t = useTeach();
    // The spotlight goes first; the card waits until it has settled.
    const eligible = t.ready && t.spotSettled && when && introEligible(key, t.state, t.role);
    const [dismissed, setDismissed] = useState(false);
    const { claim, release, active } = t;
    // Re-run when the slot frees up, so the next card in line takes it.
    useEffect(() => {
        if (eligible && !dismissed) claim(key);
        else release(key);
    }, [eligible, dismissed, key, claim, release, active]);
    useEffect(() => () => release(key), [key, release]);
    const show = eligible && !dismissed && t.active === key;
    return {
        show,
        done: () => {
            t.markSeen(key);
            setDismissed(true);
        },
        // Escape / outside: not seen — it shows again next time.
        later: () => setDismissed(true),
    };
}

/** "Opening this screen is using it" — markUsed once `when` is true. */
export function useTeachUsed(key: string, when: boolean): void {
    const { markUsed } = useTeach();
    useEffect(() => {
        if (when) markUsed(key);
    }, [when, key, markUsed]);
}

/**
 * Techs see the Help Desk spotlight only on the iPad home screen and never
 * mid-visit. The home screen calls this with `ok` = "not at a job right now".
 */
export function useTechSpotlightHere(ok: boolean): void {
    const { setTechSpotOk } = useTeach();
    useEffect(() => {
        setTechSpotOk(ok);
        return () => setTechSpotOk(false);
    }, [ok, setTechSpotOk]);
}
